import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import {
  ADD_TO_CART,
  EDIT_CART_ITEM_QUANTITY,
  REMOVE_CART_ITEM,
  SET_WEBSHOP_SHIPPING,
  SET_IS_WALLET_FROZEN
} from '@/utils/store/mutationTypes'
import {
  GET_SHIPPING_DETAILS,
  GET_MEMBER_STATS,
  CREATE_LOYALTY_ORDER_BUNDLE,
  PAY_LOYALTY_ORDER_BUNDLE
} from '@/utils/store/actionTypes'
import { addLocalCurrency } from '@/utils/general'
import { addToCartEvent, webshopPageEvent } from '@/utils/webshop'
import { AUTH_ID, USER_COUNTRY } from '@/utils/store/getterTypes'
import { idPrefixes } from '@/enums/signup'
import { QueryParams, Currencies } from '@/enums/general'
import { WebshopQueryParams } from '@/enums/webshop'

export default {
  computed: {
    cartTotal () {
      return this.cart.reduce((acc, cartItem) => acc + (cartItem.price * cartItem.quantity), 0)
    },
    receiptProps () {
      return {
        cartTotal: this.cartTotal,
        credits: this.spaazaUser.amount,
        shippingCosts: addLocalCurrency(this.shipping?.priceInCurrencyOfCountry || 0, this.country, this.shipping?.currencyOfCountry || 'EUR').replace(/\s/g, ''),
        country: this.country
      }
    },
    totalCartItems () {
      return this.cart.reduce((acc, cartItem) => acc + cartItem.quantity, 0)
    },
    country () {
      if (this.userCountry) {
        return this.userCountry
      }

      return this.appCountry
    },
    ...mapState('webshop', {
      cart: state => state.cart,
      shipping: state => state.shipping,
      shippingAddress: state => state.address,
      phoneNumber: state => state.phoneNumber,
      isWalletFrozen: state => state.isWalletFrozen
    }),
    ...mapState('spaaza', {
      spaazaUser: state => state
    }),
    ...mapGetters('auth', {
      userId: AUTH_ID,
      userCountry: USER_COUNTRY
    }),
    ...mapState('auth', {
      userEmail: state => state.user?.email
    }),
    ...mapState('app', {
      locale: state => state.locale,
      appCountry: state => state.country
    })
  },
  mounted () {
    webshopPageEvent({
      country: this.country,
      locale: this.locale,
      userId: this.userId
    })
  },
  methods: {
    addToCart (cartItem) {
      const mustHaveCartItemProps = [
        'price',
        'name',
        'sizeLabel',
        'productId',
        'slug',
        'quantity',
        'color',
        'category'
      ]

      const cartItemProps = Object.keys(cartItem)

      const hasAllProperties = mustHaveCartItemProps.every(prop => cartItemProps.includes(prop))

      if (hasAllProperties) {
        const productAlreadyInCart = this.cart.find(product => product.productId === cartItem.productId)

        if (productAlreadyInCart) {
          this.editCartItemQuantity({
            quantity: productAlreadyInCart.quantity + 1,
            productId: productAlreadyInCart.productId
          })
        } else {
          this.addToCartMutation({ cartItem })
        }
      } else {
        throw new Error('Cart Item is missing Props')
      }

      addToCartEvent([...this.cart])
    },
    addOneItemQuantity ({ productId, quantity, price }) {
      const canAddProductToCard = ((this.spaazaUser.amount - this.cartTotal - price) >= 0)

      if (canAddProductToCard) {
        this.editCartItemQuantity({ productId, quantity: quantity + 1 })

        addToCartEvent([...this.cart])
      } else {
        this.$otoaster.warning(this.$t('Loyalty.Webshop.ProductDetail.NotEnoughCredits'))
      }
    },
    removeOneItemQuantity ({ productId, quantity }) {
      if (quantity > 1) {
        this.editCartItemQuantity({ productId, quantity: quantity - 1 })
      } else {
        this.removeCartItem({ productId })
      }
    },
    async getAndSetShippingDetails () {
      try {
        if (this.shipping?.currencyOfCountry !== Currencies[this.country]) {
          const shipping = await this.getShippingDetails()

          this.setWebshopShipping({ shipping })
        }
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    async getAndSetMemberStats () {
      try {
        const { getMemberStatsForMemberStatisticsForUser } = await this.getMemberStats()
        const isWalletFrozen = getMemberStatsForMemberStatisticsForUser.numberOfPausedMembers > 0 && getMemberStatsForMemberStatisticsForUser.numberOfActiveMembers < 1

        this.setIsWalletFrozen({ isWalletFrozen })
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    async createAndPayOrderBundle ({
      products = [],
      streetType = '',
      street = '',
      houseNumber = '',
      houseNumberAddition = '',
      phoneNumber = '',
      building = '',
      floor = '',
      postalCode = '',
      city = '',
      area = '',
      region,

      issuerId = null,
      deviceData = null,
      paymentMethodNonce = null,
      paymentType = '',
      successUrl = '',
      failedUrl = '',
      pendingUrl = ''
    }) {
      const orderBundleId = await this.createLoyaltyOrderBundle({
        products,
        streetType,
        street,
        houseNumber,
        houseNumberAddition,
        phoneNumber,
        building,
        floor,
        postalCode,
        city,
        area,
        region
      })

      const redirectUrl = await this.payLoyaltyOrderBundle({
        orderBundle: `${idPrefixes.orderBundle}${orderBundleId}`,
        issuerId,
        deviceData,
        paymentMethodNonce,
        paymentType,
        successUrl: `${successUrl}?${QueryParams.orderBundleId}=${orderBundleId}&${WebshopQueryParams.credits}=${this.spaazaUser.amount}`,
        failedUrl,
        pendingUrl
      })

      window.location = redirectUrl
    },
    ...mapMutations('webshop', {
      addToCartMutation: ADD_TO_CART,
      editCartItemQuantity: EDIT_CART_ITEM_QUANTITY,
      removeCartItem: REMOVE_CART_ITEM,
      setWebshopShipping: SET_WEBSHOP_SHIPPING,
      setIsWalletFrozen: SET_IS_WALLET_FROZEN
    }),
    ...mapActions('member', {
      getMemberStats: GET_MEMBER_STATS
    }),
    ...mapActions('webshop', {
      getShippingDetails: GET_SHIPPING_DETAILS,
      createLoyaltyOrderBundle: CREATE_LOYALTY_ORDER_BUNDLE,
      payLoyaltyOrderBundle: PAY_LOYALTY_ORDER_BUNDLE
    })
  }
}
